/**
 * 🎵 SYSTÈME DE RÉCOMPENSES PROGRESSIVES
 * Sons et effets visuels à chaque étape de complétion du profil
 * 
 * Étapes:
 * - 📸 Photo validée → Son "ding"
 * - ✍️ Bio ajoutée → Tintillement
 * - 💫 Passions → Effet magique
 * - ✨ Avatar IA → Son célébration
 * - 🎆 100% complet → FEU D'ARTIFICE + Confettis
 */

import { useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';

// 🎵 URLs des sons (sons courts encodés en base64 pour éviter les problèmes de chargement)
// Utilisation de Web Audio API pour générer des sons synthétiques
const createAudioContext = () => {
  if (typeof window === 'undefined') return null;
  return new (window.AudioContext || window.webkitAudioContext)();
};

// Générateur de sons synthétiques
const playTone = (frequency, duration, type = 'sine', volume = 0.3) => {
  try {
    const audioContext = createAudioContext();
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.value = volume;

    // Fade out
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (e) {
    console.log('Audio not supported');
  }
};

// 🔊 Sons prédéfinis
const SOUNDS = {
  // Son "ding" simple pour photo
  photo: () => {
    playTone(880, 0.15, 'sine', 0.2);
    setTimeout(() => playTone(1100, 0.2, 'sine', 0.15), 100);
  },
  
  // Tintillement pour bio
  bio: () => {
    playTone(660, 0.1, 'sine', 0.15);
    setTimeout(() => playTone(880, 0.1, 'sine', 0.15), 80);
    setTimeout(() => playTone(1100, 0.15, 'sine', 0.1), 160);
  },
  
  // Effet magique pour passions
  passions: () => {
    playTone(440, 0.1, 'triangle', 0.2);
    setTimeout(() => playTone(550, 0.1, 'triangle', 0.2), 100);
    setTimeout(() => playTone(660, 0.1, 'triangle', 0.2), 200);
    setTimeout(() => playTone(880, 0.2, 'triangle', 0.15), 300);
  },
  
  // Célébration pour avatar IA
  avatar: () => {
    playTone(523, 0.15, 'sine', 0.2);
    setTimeout(() => playTone(659, 0.15, 'sine', 0.2), 150);
    setTimeout(() => playTone(784, 0.2, 'sine', 0.2), 300);
  },
  
  // Fanfare pour 100% (feu d'artifice)
  complete: () => {
    // Accord majeur ascendant
    playTone(523, 0.2, 'sine', 0.25); // Do
    setTimeout(() => playTone(659, 0.2, 'sine', 0.25), 150); // Mi
    setTimeout(() => playTone(784, 0.2, 'sine', 0.25), 300); // Sol
    setTimeout(() => playTone(1047, 0.4, 'sine', 0.3), 450); // Do octave
    // Deuxième vague
    setTimeout(() => {
      playTone(1175, 0.15, 'sine', 0.2);
      playTone(1319, 0.15, 'sine', 0.2);
      playTone(1568, 0.3, 'sine', 0.25);
    }, 700);
  },
  
  // Son d'erreur
  error: () => {
    playTone(200, 0.2, 'sawtooth', 0.15);
    setTimeout(() => playTone(150, 0.3, 'sawtooth', 0.1), 200);
  },
  
  // Click simple
  click: () => {
    playTone(800, 0.05, 'sine', 0.1);
  }
};

// 🎆 Effets de confettis
const CONFETTI_EFFECTS = {
  // Confettis simples
  simple: () => {
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 }
    });
  },
  
  // Confettis des deux côtés
  sides: () => {
    confetti({
      particleCount: 30,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 }
    });
    confetti({
      particleCount: 30,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 }
    });
  },
  
  // 🎆 FEU D'ARTIFICE COMPLET pour 100%
  fireworks: () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#ff69b4', '#8b5cf6', '#06b6d4', '#fbbf24', '#10b981'];

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      // Plusieurs explosions
      confetti({
        particleCount: 30,
        startVelocity: 30,
        spread: 360,
        ticks: 60,
        origin: {
          x: Math.random(),
          y: Math.random() * 0.5
        },
        colors: colors,
        shapes: ['circle', 'square'],
        gravity: 0.8
      });
    }, 250);

    // Explosion finale
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 100,
        origin: { x: 0.5, y: 0.5 },
        colors: colors
      });
    }, duration - 500);
  },
  
  // Étoiles
  stars: () => {
    confetti({
      particleCount: 40,
      spread: 70,
      origin: { y: 0.6 },
      shapes: ['star'],
      colors: ['#ffd700', '#ffb700', '#ff9500']
    });
  }
};

/**
 * Hook pour gérer les récompenses de profil
 */
export const useProfileRewards = () => {
  const lastCompletionRef = useRef(0);
  const playedRewardsRef = useRef(new Set());

  // 🎵 Jouer un son
  const playSound = useCallback((soundName) => {
    const soundFn = SOUNDS[soundName];
    if (soundFn) {
      soundFn();
    }
  }, []);

  // 🎆 Déclencher un effet de confettis
  const triggerConfetti = useCallback((effectName) => {
    const effectFn = CONFETTI_EFFECTS[effectName];
    if (effectFn) {
      effectFn();
    }
  }, []);

  // 📊 Vérifier et déclencher les récompenses basées sur la complétion
  const checkAndReward = useCallback((profile, newCompletion) => {
    const oldCompletion = lastCompletionRef.current;
    lastCompletionRef.current = newCompletion;

    // Pas de récompense si pas de changement positif
    if (newCompletion <= oldCompletion) return;

    // 🎆 100% COMPLET - FEU D'ARTIFICE !
    if (newCompletion >= 100 && oldCompletion < 100) {
      playSound('complete');
      setTimeout(() => triggerConfetti('fireworks'), 300);
      return { type: 'complete', message: '🎆 Profil 100% complet ! Tu es prêt(e) à matcher !' };
    }

    // Vérifier les étapes individuelles
    const rewards = [];

    // 📸 Photo ajoutée
    if (profile.avatarImage && !playedRewardsRef.current.has('avatarImage')) {
      playedRewardsRef.current.add('avatarImage');
      playSound('avatar');
      triggerConfetti('stars');
      rewards.push({ type: 'avatar', message: '✨ Superbe avatar IA !' });
    }

    // 📸 Photo de profil
    if ((profile.photos?.length > 0 || profile.mainPhoto) && !playedRewardsRef.current.has('photo')) {
      playedRewardsRef.current.add('photo');
      playSound('photo');
      triggerConfetti('simple');
      rewards.push({ type: 'photo', message: '📸 Photo ajoutée !' });
    }

    // ✍️ Bio ajoutée
    if (profile.bio && profile.bio.length > 10 && !playedRewardsRef.current.has('bio')) {
      playedRewardsRef.current.add('bio');
      playSound('bio');
      rewards.push({ type: 'bio', message: '✍️ Bio validée !' });
    }

    // 💫 Passions complètes (au moins 5)
    const passionCount = profile.passions ? Object.values(profile.passions).flat().length : 0;
    if (passionCount >= 5 && !playedRewardsRef.current.has('passions')) {
      playedRewardsRef.current.add('passions');
      playSound('passions');
      triggerConfetti('sides');
      rewards.push({ type: 'passions', message: '💫 Passions révélées !' });
    }

    return rewards.length > 0 ? rewards[0] : null;
  }, [playSound, triggerConfetti]);

  // 🔄 Reset les récompenses jouées (pour nouveau profil)
  const resetRewards = useCallback(() => {
    playedRewardsRef.current.clear();
    lastCompletionRef.current = 0;
  }, []);

  // 🎮 Jouer une récompense spécifique manuellement
  const playReward = useCallback((rewardType) => {
    switch (rewardType) {
      case 'photo':
        playSound('photo');
        triggerConfetti('simple');
        break;
      case 'bio':
        playSound('bio');
        break;
      case 'passions':
        playSound('passions');
        triggerConfetti('sides');
        break;
      case 'avatar':
        playSound('avatar');
        triggerConfetti('stars');
        break;
      case 'complete':
        playSound('complete');
        setTimeout(() => triggerConfetti('fireworks'), 300);
        break;
      case 'click':
        playSound('click');
        break;
      case 'error':
        playSound('error');
        break;
      default:
        break;
    }
  }, [playSound, triggerConfetti]);

  return {
    playSound,
    triggerConfetti,
    checkAndReward,
    resetRewards,
    playReward
  };
};

export default useProfileRewards;
